/**
 * Edit Partner Network Modal Component
 *
 * Allows users to select which partner trades they want in their network.
 * Minimum of 12 trades must be selected.
 * Saves selections to the user_partner_trade_preferences table.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { useTradeSections, TradeSection, Trade, getSelectableTrades } from '@/hooks/useTrades';
import {
  usePartnerTradePreferences,
  partnerTradePreferencesQueryKey,
} from '@/hooks/usePartnerTradePreferences';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';

const MINIMUM_TRADES = 12;

interface EditPartnerNetworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfileId: string;
  onSuccess?: () => void;
}

export default function EditPartnerNetworkModal({
  isOpen,
  onClose,
  userProfileId,
  onSuccess,
}: EditPartnerNetworkModalProps) {
  const [selectedTrades, setSelectedTrades] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Fetch all trade categories grouped by section using the working lookup endpoint.
  // Scoped to `isOpen` for the same reason as the preferences query below: NetworkHealthCard
  // renders this modal closed as soon as its progress query resolves, so an unscoped lookup
  // fired a ~290ms request on every /network load — after the rest of the page had already
  // come back — for a dialog nobody had opened.
  const {
    data: tradeSections,
    isLoading: isLoadingCategories,
  } = useTradeSections(isOpen);

  // Get all selectable trades (non-section-headers) for Select All functionality
  const allSelectableTrades = tradeSections
    ? tradeSections.flatMap(section => getSelectableTrades(section.trades))
    : [];

  // Fetch the user's currently saved preferences. Only requested while the modal is open —
  // this is the data the checkboxes are seeded from.
  const {
    data: savedTradeCategoryIds,
    isLoading: isLoadingPreferences,
    isFetching: isFetchingPreferences,
  } = usePartnerTradePreferences(userProfileId, isOpen);

  // Seed the checkboxes from the saved preferences once per open. The ref guard means a
  // background refetch while the modal is open can't wipe out edits in progress, and
  // resetting it on close means Cancel discards local edits instead of persisting them
  // into the next open.
  const hasSeededSelectionRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasSeededSelectionRef.current = false;
      return;
    }

    // Wait for fresh data — seeding from a stale cache entry would show the selections
    // as they were before the last save.
    if (hasSeededSelectionRef.current || isFetchingPreferences || !savedTradeCategoryIds) {
      return;
    }

    setSelectedTrades(new Set(savedTradeCategoryIds));
    setHasChanges(false);
    hasSeededSelectionRef.current = true;
  }, [isOpen, isFetchingPreferences, savedTradeCategoryIds]);

  const handleToggleTrade = (tradeId: string) => {
    setSelectedTrades((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tradeId)) {
        newSet.delete(tradeId);
      } else {
        newSet.add(tradeId);
      }
      return newSet;
    });
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    if (allSelectableTrades.length > 0) {
      const allIds = new Set(allSelectableTrades.map((t: Trade) => t.id));
      setSelectedTrades(allIds);
      setHasChanges(true);
    }
  };

  const handleClearAll = () => {
    setSelectedTrades(new Set());
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (selectedTrades.size < MINIMUM_TRADES) {
      showError('Minimum Selection Required', `Please select at least ${MINIMUM_TRADES} trades.`);
      return;
    }

    setIsSaving(true);

    try {
      // Persist the full selection in one idempotent request. The backend diffs against
      // the authoritative DB state and inserts with conflict-ignore, so re-saving an
      // existing set can't collide with the unique (user_profile_id, trade_category_id)
      // index — which is what previously caused the per-row inserts to 500.
      await api.put('/api/workflows/partner-network-progress/preferences', {
        tradeCategoryIds: [...selectedTrades],
      });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: partnerTradePreferencesQueryKey });
      queryClient.invalidateQueries({ queryKey: ['user_partner_trade_preferences'] });
      queryClient.invalidateQueries({ queryKey: ['partner-network-progress'] });

      showSuccess('Preferences Saved', 'Your partner network preferences have been updated.');
      setHasChanges(false);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      showError('Save Failed', 'Failed to save your preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmClose) return;
    }
    onClose();
  };

  const isLoading = isLoadingCategories || isLoadingPreferences;
  const isValid = selectedTrades.size >= MINIMUM_TRADES;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-800">
            Edit Your Partner Network
          </DialogTitle>
          <DialogDescription className="sr-only">
            Select the trades you want partners for in your network
          </DialogDescription>
        </DialogHeader>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Build Your Partner Network</p>
              <p>
                Select the trades you want to find partners for in your network. You must select at
                least <strong>{MINIMUM_TRADES} trades</strong> to ensure you have a diverse network
                of partners who can help with your referrals.
              </p>
            </div>
          </div>
        </div>

        {/* Selection Status */}
        <div className="flex items-center justify-between mb-4 px-1">
          <div className="flex items-center gap-2">
            {isValid ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-500" />
            )}
            <span
              className={`text-sm font-medium ${isValid ? 'text-green-700' : 'text-amber-600'}`}
            >
              {selectedTrades.size} of {MINIMUM_TRADES} minimum trades selected
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={isLoading || isSaving}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              disabled={isLoading || isSaving}
            >
              Clear All
            </Button>
          </div>
        </div>

        {/* Trade Categories List - Organized by Section */}
        <div className="flex-1 overflow-y-auto border rounded-lg">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-5 w-5" />
                  <Skeleton className="h-5 flex-1" />
                </div>
              ))}
            </div>
          ) : (
            <div>
              {tradeSections?.map((section: TradeSection) => {
                const selectableTrades = getSelectableTrades(section.trades);
                if (selectableTrades.length === 0) return null;

                return (
                  <div key={section.section}>
                    {/* Section Header */}
                    <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 sticky top-0">
                      <h4 className="text-sm font-semibold text-gray-700">{section.section}</h4>
                    </div>
                    {/* Trades in this section */}
                    <div>
                      {selectableTrades.map((trade: Trade) => (
                        <label
                          key={trade.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <Checkbox
                            checked={selectedTrades.has(trade.id)}
                            onCheckedChange={() => handleToggleTrade(trade.id)}
                            disabled={isSaving}
                          />
                          <span className="text-sm text-gray-700">{trade.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
              {(!tradeSections || tradeSections.length === 0) && (
                <div className="p-8 text-center text-gray-500">
                  No trade categories available.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || isSaving || !hasChanges}
            className="min-w-[100px]"
          >
            {isSaving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
